import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { api, ApiError, errorText, type PlanCommitResult, type PlanDto } from "../api/client";
import { OperationCard } from "../components/OperationEditor";
import { Questions } from "../components/Questions";
import { relativeTime } from "../format";

const reasonLabels: Record<string, string> = {
  open_questions: "Es sind noch Fragen offen.",
  blocking_issues: "Mindestens eine Aktion ist so nicht ausführbar.",
  no_operations: "Keine Aktion ausgewählt.",
  destructive: "Enthält Löschungen/Absagen.",
  series_or_update: "Ändert bestehende Termine oder Serien.",
  large_batch: "Viele Änderungen auf einmal.",
  media_source: "Aus Bild/Dokument erkannt – bitte genau prüfen.",
  calendar_change_preview: "Kalenderänderungen werden standardmäßig vorab angezeigt.",
  uncertain_values: "Einige Werte wurden unsicher erkannt.",
  reversal: "Rücknahme einer früheren Änderung.",
  conflict_retry: "Daten haben sich geändert – neue Prüfung nötig.",
  token_scope_requires_review: "Über ChatGPT/Codex eingereicht – Freigabe hier nötig.",
};

/** Quelle und Ergebnis nebeneinander; die Freigabe ist ein Serverdatensatz, gebunden an Version und Hash. */
export function ReviewPage() {
  const { planId } = useParams();
  const qc = useQueryClient();
  const plan = useQuery({ queryKey: ["plan", planId], queryFn: () => api<PlanDto>(`/api/v1/action-plans/${planId}`) });
  const [edits, setEdits] = useState<Record<number, Record<string, unknown>>>({});
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PlanCommitResult | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setEdits({});
    setSelected({});
  }, [plan.data?.planVersion]);

  const dirty = Object.keys(edits).length > 0 || Object.keys(selected).length > 0;

  const saveEdits = useMutation({
    mutationFn: () =>
      api<PlanDto>(`/api/v1/action-plans/${planId}`, {
        method: "PATCH",
        body: {
          expectedPlanVersion: plan.data!.planVersion,
          operations: plan.data!.operations
            .filter((o) => edits[o.index] || selected[o.index] !== undefined)
            .map((o) => ({ index: o.index, payload: edits[o.index] ? { ...(o.payload as object), ...edits[o.index] } : null, selected: selected[o.index] ?? null })),
        },
      }),
    onSuccess: (p) => qc.setQueryData(["plan", planId], p),
    onError: (e) => setError(errorText(e)),
  });

  const approve = useMutation({
    mutationFn: () => api<PlanCommitResult>(`/api/v1/action-plans/${planId}/approve-and-commit`, { body: { planVersion: plan.data!.planVersion, planHash: plan.data!.planHash } }),
    onSuccess: async (r) => {
      setResult(r);
      await qc.invalidateQueries();
    },
    onError: async (e) => {
      setError(e instanceof ApiError && e.code === "plan_conflict" ? "Daten haben sich inzwischen geändert. Die Vorschau wurde aktualisiert – bitte erneut prüfen." : errorText(e));
      await qc.invalidateQueries({ queryKey: ["plan", planId] });
    },
  });

  const reject = useMutation({ mutationFn: () => api(`/api/v1/action-plans/${planId}/reject`, { method: "POST", body: {} }), onSuccess: () => qc.invalidateQueries() });

  if (plan.isPending) return <div className="page muted">Lade Vorschlag …</div>;
  if (plan.isError) return <div className="page error">{errorText(plan.error)}</div>;
  const p = plan.data;
  const editable = p.status === "awaiting_review" || p.status === "needs_answer";

  return (
    <div className="page review">
      <h1>Vorschlag prüfen</h1>
      {p.summary && <p className="lead">{p.summary}</p>}
      <div className="review-grid">
        <section className="card source" aria-label="Quelle">
          <h2>Quelle</h2>
          {p.source ? (
            <>
              {p.source.text && <blockquote>{p.source.text}</blockquote>}
              {p.source.transcript && <blockquote>🎙️ {p.source.transcript}</blockquote>}
              {p.source.clientSummary && <blockquote className="muted">Zusammenfassung des Clients (Original nicht verfügbar): {p.source.clientSummary}</blockquote>}
              <div className="thumbs large">
                {p.source.assets.filter((a) => a.hasPreview).map((a) => (
                  <a key={a.id} href={`/api/v1/assets/${a.id}/preview`} target="_blank" rel="noreferrer"><img src={`/api/v1/assets/${a.id}/preview`} alt="Quellbild" /></a>
                ))}
              </div>
              <p className="muted small">Bezugszeit für „heute/morgen“: {new Date(p.source.anchor).toLocaleString("de-DE")} ({p.source.anchorSource === "client" ? "Erfassung am Gerät" : "Eingang am Server"}), Zeitzone {p.source.timeZone}</p>
            </>
          ) : (
            <p className="muted">Keine Quelle.</p>
          )}
        </section>
        <section className="card result" aria-label="Ergebnis">
          <h2>Ergebnis <span className="muted small">Version {p.planVersion}</span></h2>
          {p.inputId && editable && <Questions inputId={p.inputId} questions={p.questions} />}
          <ol className="ops">
            {p.operations.map((op) => (
              <OperationCard key={`${p.planVersion}-${op.index}`} op={op} editable={editable && editing}
                selected={selected[op.index] ?? op.selected}
                onSelect={(v) => setSelected({ ...selected, [op.index]: v })}
                onEdit={(patch) => setEdits({ ...edits, [op.index]: { ...edits[op.index], ...patch } })} />
            ))}
          </ol>
        </section>
      </div>

      {result ? (
        <div className="card ok-box">
          <p><strong>Gespeichert.</strong> Beleg {result.receipt.commitId.slice(0, 8)} · Revision {String(result.receipt.revision)} · {relativeTime(result.receipt.committedAt)}{result.replayed ? " (bereits zuvor gespeichert)" : ""}</p>
          <Link to="/inbox" className="btn">Zum Eingang</Link>
        </div>
      ) : p.status === "committed" ? (
        <p className="ok">Bereits gespeichert{p.receipt ? ` (Beleg ${p.receipt.commitId.slice(0, 8)})` : ""}.</p>
      ) : editable ? (
        <div className="card approve-box">
          {p.approvalReasons.length > 0 && (
            <ul className="reasons">{p.approvalReasons.map((r) => <li key={r}>{reasonLabels[r] ?? r}</li>)}</ul>
          )}
          {error && <p className="error">{error}</p>}
          <div className="row wrap">
            {dirty && <button className="btn" disabled={saveEdits.isPending} onClick={() => saveEdits.mutate()}>Änderungen übernehmen</button>}
            <button className="btn primary" disabled={dirty || p.approvalClass === "blocked" || approve.isPending} onClick={() => approve.mutate()}
              title={p.approvalClass === "blocked" ? "Erst Rückfragen beantworten bzw. Fehler beheben" : undefined}>
              {approve.isPending ? "Speichere …" : "Bestätigen"}
            </button>
            <button className="btn" aria-pressed={editing} onClick={() => setEditing(!editing)}>Ändern</button>
            <button className="btn ghost" disabled={reject.isPending} onClick={() => reject.mutate()}>Löschen</button>
          </div>
        </div>
      ) : (
        <p className="muted">Status: {p.status}</p>
      )}
    </div>
  );
}
